import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import * as settingsApi from '../endpoints';
import { useAppSelector } from '../../../store/hooks';
import { selectAuthUser } from '../../../store/selectors/authSelectors';

export default function AccountTab({
  accountForm,
  setAccountForm,
  showNewPassword
}) {
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const user = useAppSelector(selectAuthUser);

  const handleSaveProfile = async () => {
    // Validate phone number (must be exactly 10 digits if provided)
    if (accountForm.phone) {
      const digits = accountForm.phone.replace(/\D/g, '');
      if (digits.length !== 10) {
        toast.error('Phone number must be exactly 10 digits');
        return;
      }
    }

    setSavingProfile(true);
    try {
      await settingsApi.updateAccount({
        user_id: user?.id,
        first_name: accountForm.firstName,
        last_name: accountForm.lastName,
        email: accountForm.email,
        phone: accountForm.phone
      });
      toast.success('Profile information saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save profile settings');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSavePassword = async () => {
    if (!accountForm.newPassword || !accountForm.confirmPassword) {
      toast.error('Please fill in both password fields');
      return;
    }
    if (accountForm.newPassword !== accountForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (accountForm.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    setSavingPassword(true);
    try {
      await settingsApi.changePassword({
        user_id: user?.id,
        new_password: accountForm.newPassword
      });
      toast.success('Password updated successfully');
      setAccountForm(prev => ({ ...prev, currentPassword: '', newPassword: '', confirmPassword: '' }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update password');
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Profile Information Card */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg">Profile Information</CardTitle>
          <CardDescription>Update your personal details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">Name</Label>
              <Input
                id="firstName"
                value={accountForm.firstName}
                onChange={(e) => setAccountForm(prev => ({ ...prev, firstName: e.target.value }))}
                data-testid="account-firstname-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                value={accountForm.lastName}
                onChange={(e) => setAccountForm(prev => ({ ...prev, lastName: e.target.value }))}
                data-testid="account-lastname-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={accountForm.email}
                onChange={(e) => setAccountForm(prev => ({ ...prev, email: e.target.value }))}
                disabled
                data-testid="account-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                value={accountForm.phone}
                onChange={(e) => setAccountForm(prev => ({ ...prev, phone: e.target.value }))}
                className={
                  accountForm.phone && accountForm.phone.replace(/\D/g, '').length !== 10
                    ? 'border-destructive focus-visible:ring-destructive'
                    : ''
                }
                placeholder="+1 (555) 000-0000"
                data-testid="account-phone-input"
              />
              {accountForm.phone && accountForm.phone.replace(/\D/g, '').length !== 10 && (
                <p className="text-xs text-destructive">Phone number must be exactly 10 digits</p>
              )}
            </div>
          </div>
          <div className="flex justify-end pt-4 border-t">
            <Button onClick={handleSaveProfile} disabled={savingProfile} className="bg-primary hover:bg-primary/90" data-testid="save-profile-btn">
              {savingProfile ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Profile
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Change Password Card */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg">Change Password</CardTitle>
          <CardDescription>Update your password to keep your account secure</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <Input
                id="newPassword"
                type={showNewPassword ? 'text' : 'password'}
                value={accountForm.newPassword}
                onChange={(e) => setAccountForm(prev => ({ ...prev, newPassword: e.target.value }))}
                className={
                  (accountForm.newPassword && accountForm.newPassword.length < 8) ||
                    ((accountForm.newPassword || accountForm.confirmPassword) && accountForm.newPassword !== accountForm.confirmPassword)
                    ? 'border-destructive focus-visible:ring-destructive'
                    : ''
                }
                data-testid="new-password-input"
              />
              {accountForm.newPassword && accountForm.newPassword.length < 8 && (
                <p className="text-xs text-destructive">Password must be at least 8 characters long</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={accountForm.confirmPassword}
                onChange={(e) => setAccountForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                className={
                  (accountForm.newPassword || accountForm.confirmPassword) && accountForm.newPassword !== accountForm.confirmPassword
                    ? 'border-destructive focus-visible:ring-destructive'
                    : ''
                }
                data-testid="confirm-password-input"
              />
              {(accountForm.newPassword || accountForm.confirmPassword) && accountForm.newPassword !== accountForm.confirmPassword && (
                <p className="text-xs text-destructive">Passwords do not match</p>
              )}
            </div>
          </div>
          <div className="flex justify-end pt-4 border-t">
            <Button onClick={handleSavePassword} disabled={savingPassword} className="bg-primary hover:bg-primary/90" data-testid="save-password-btn">
              {savingPassword ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Update Password
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
